import { LegalEntityIdentifier } from '../static-data/models/consts/legal-entity-identifier.const';
import { LegalEntity } from '../static-data/models/consts/legal-entity.const';
import { CalculatedFee } from './calculated-fees/calculated-fees.consts';
import {
  Distributor,
  DistributorDetails,
  DistributorGroups,
} from './distributor.const';
import { TradeDeliveranceReference } from './fee-calculation/fee-calculation.consts';
import { FXRates, PricesAndPositions } from './fees-data-upload/fees-data-upload.consts';
import { MasterAgreement } from './master-agreements/master-agreements.consts';
import { SupplementAgreementRequests } from './supplement-agreement-requests/supplement-agreement-requests.consts';
import { FeeCalculation } from './supplement-agreement/supplement-agreement-fee.model';
import { SupplementAgreement } from './supplement-agreement/supplement-agreement.consts';


type StringOrObject = string | { [key: string]: string };

export type LabelsOf<T> = {
  [K in keyof T]?: 
    T[K] extends Array<infer U> ? 
      U extends object ? (LabelsOf<U> | string) : StringOrObject
    : T[K] extends object ? 
      LabelsOf<T[K]> | string
    : StringOrObject;
} & {
  [key: string]: StringOrObject;
};


export type FeesLabels = {
  distributorGroup: LabelsOf<DistributorGroups>;
  distributor: LabelsOf<Distributor & DistributorDetails>;
  masterAgreement: LabelsOf<MasterAgreement>;
  supplementAgreement: LabelsOf<SupplementAgreement>;
  supplementAgreementRequests: LabelsOf<SupplementAgreementRequests>;
  legalEntity: LabelsOf<LegalEntity> & LabelsOf<LegalEntityIdentifier>;
  fxRates?: LabelsOf<FXRates>;
  pricesAndPositions?: LabelsOf<PricesAndPositions>
  feeCalculation?: LabelsOf<FeeCalculation>;
  calculatedFees?: LabelsOf<CalculatedFee>;
  tradeDeliveryReference?: LabelsOf<TradeDeliveranceReference>;
};

export const FEES_MANAGEMENT_LABELS = {
  distributorGroup: {
    id: 'ID',
    name: 'Name',
    status: 'Status',
    details: 'Details',
    workflowStatus: 'Workflow Status',
    salesContactId: 'Sales Contact',
    comments: 'Comments',
    contacts: 'Contacts',
    distributorGroupId: 'Distributor Group',
    contact: {
      title: 'Title',
      firstName: 'First Name',
      lastName: 'Last Name',
      preferredLanguage: 'Preferred Language',
      phone: 'Phone No.',
      email: 'Email Address',
      role: 'Role',
      workflowStatus: 'Workflow Status',
    },
  },
  distributor: {
    name: 'Distributor Name',
    distributorDetails: 'Distributor Details',
    distributorGroup: 'Distributor Group',
    identifiers: 'Distributor Identifiers',
    identifierCode: 'Code',
    identifierValue: 'Value',
    address: 'Address',
    country: 'Country',
    legalEntity: 'Entity',
    nettedPayments: 'Netted Payments',
    autoReleaseOfNettingQueue: 'Auto Release of Netting Queue',
    clientStatementsTo: 'Client Statements To',
    comments: 'Comments',
    mailEncryptionPwdRef: 'Mail Encryption Password',
    distributorDetailsRef: 'Distributor Details Reference',
    id: 'Distributor ID',
    workflowStatus: 'Workflow Status',
  },
  masterAgreement: {
    id: 'ID',
    name: 'Name',
    distributorGroup: 'Distributor Group',
    agreementType: 'Type',
    agreementStatus: 'Status',
    nonSigningSupplement: 'Non-Signing Supplement',
    cooldownPeriod: 'Opposition Period (in Days)',
    setToFullyExecuted: 'Set to Fully Executed Date',
    setToTerminated: 'Set to Terminated Date',
    requestDate: 'Request Date',
    executionDate: 'Execution Date',
    agreementLanguage: 'Language',
    legalEntity: 'Legal Entity',
    salesContact: 'Sales Responsible',
    opsContact: 'Ops Responsible',
    agreementExpiryDate: 'Expiry Date',
    opsApproverContact: 'Ops Approver',
    productGovernanceApproach: 'Product Governance Approach',
    mifidIICompliant: 'MiFID II Compliant',
    applicableLaw: 'Applicable Law',
    comments: 'Comments',
    platformDistribution: 'Platform Distribution',
    internalClient: 'Internal Client',
    preferredSDADelivery: 'Preferred SDA Delivery',
    regionalSalesHeadApproval: 'Approval',
    regionalSalesHeadApprovalRequested: 'Approval Requested',
    regionalSalesHeadApprovalReceived: 'Approval Received',
    trailerPaymentPeriodAfterMDATermination:
      'Trailer Payment Period After MDA Termination (in Months)',
    terminationPeriod: 'Termination Period (in Days)',
    sellingRestrictions: 'Selling Restrictions',
    productCategory: 'Product Category',
    expectedAssetsUnderManagement: 'Expected Assets Under Management (in EUR)',
    expectedAnnualRevenues: 'Expected Annual Revenues (in EUR)',
    prioritisationOfAgreement: 'Prioritisation of Agreement',
    typeOfTemplate: 'Type of Template',
    lastUpdatedBy: 'Last Updated By',
    updatedOn: 'Updated On',
    createdBy: 'Created By',
    createdDate: 'Created Date',
  },
  supplementAgreement: {
    id: 'ID',
    fees: 'Fees',
    masterAgreement: 'Under Master Agreement',
    allowedFeeTypes: 'Allowed Fee Types',
    distributorId: 'Applicable For',
    agreementRequestDate: 'Agreement Request Date',
    agreementExecutionDate: 'Agreement Execution Date',
    agreementStatus: 'Agreement Status',
    holdingEmailsTo: 'Holding Emails To',
    setToFullyExecuted: 'Set to Fully Executed',
    setToTerminated: 'Set to Terminated',
    opsContactId: 'Ops Responsible',
    opsApproverContactId: 'Ops Approver',
    salesContactId: 'Sales Responsible',
    salesContactGroupId: 'Sales Group',
    region: 'Region',
    business: 'Business',
    inducementDisclosedToInvestorMethod:
      'Inducement Disclosure To Investor Method',
    valueAddedForInvestor: 'Value Added For Investor',
    recurringFeeConfigId: 'Trailer Evaluation Template',
    evaluationFrequency: 'Evaluation Frequency',
    productCategory: 'Product Category',
    businessDayConvention: 'Business Day Convention',
    paymentFrequency: 'Payment Frequency',
    evaluationDay: 'Evaluation Day',
    calculationMethod: 'Calculation Method',
    paymentAdvice: 'Payment Advice',
    evaluationStartDate: 'Evaluation Start Date',
    evaluationEndDate: 'Evaluation End Date',
    startOfFirstPeriod: 'Start of First Period',
    evaluationPeriodType: 'Evaluation Period Type',
    evaluationDates: 'Evaluation Dates',
    balanceSource: 'Balance Source',
    balancesEmail: 'Balances Email',
    rcsPnlEUR: 'RCS PnL (in EUR)',
    exoticsComments: 'Exotics Comment',
    exoticsCommentForClientStatement: 'Exotics Comment For Client Statement',
    comments: 'Comments',
    isin: 'ISIN',
    additionalAlternativeProvisionRequired:
      'Additional/Alternative Provision Required (Except of additional provisions for Italian Clients)?',
    hedgeFundAndOrCreditRelated: 'Hedge Fund and/or Credit Related?',
    holdingPeriodApplicable: 'Holding Period Applicable?',
    createdBy: 'Created By',
    createdDate: 'Created Date',
    lastUpdatedBy: 'Last Updated By',
    updatedOn: 'Updated On',
    inducementDisclosedToInvestorMethodOther: 'Inducement Disclosure To Investor Method (Details)',
    valueAddedForInvestorOther: 'Value Added For Investor (Details)',
  },
  supplementAgreementRequests: {
    id: 'ID',
    productSetupRequired: 'Product Setup Required?',
    distributorSetupRequired: 'Distributor Setup Required?',
    masterAgreementSetupRequired: 'Master Agreement Setup Required?',
    productIsin: 'ISIN',
    productIsins: 'ISIN',
    productCategory: 'Category',
    productIssuerId: 'Issuer',
    productSalesContactId: 'Sales Contact',
    productBusinessArea: 'Business Area',
    productWrapper: 'Wrapper',
    productDbTicketRef: 'dbTicket Reference',
    distributorId: 'Distributor',
    distributorName: 'Distributor Name',
    distributorAddress: 'Distributor Address',
    distributorDbTraderNo: 'dbTrader No.',
    distributorCountry: 'Distributor Country',
    masterAgreementId: 'Under Master Agreement',
    masterAgreementName: 'Master Agreement Name',
    masterAgreementType: 'Master Agreement Type',
    allowedFeeTypes: 'Fee Types',
    business: 'Business',
    region: 'Region',
    inducementDisclosureToInvestorMethod:
      'Inducement Disclosure To Investor Method',
    valueAddedForInvestor: 'Value Added For Investor',
    comments: 'Comments',
    salesContactId: 'Sales Contact',
    salesContactEmail: 'Sales Contact Email',
    salesTeam: 'Sales Team',
    deliveryChannel: 'Preferred SDA Delivery',
    signingRequired: 'Signing Required',
    opsContactId: 'Ops Responsible',
    opsApproverContactId: 'Ops Approver',
    status: 'Status',
  },
  legalEntity: {
    code: 'Code',
    name: 'Name',
    shortName: 'Short Name',
    type: 'Type',
    id: 'ID',
  },
  pricesAndPositions: {
    isin: 'ISIN',
    price: 'Price',
    position: 'Position',
    actualDate: 'Actual Date',
  },
  fxRates: {
    date: 'Date',
    baseCurrency: 'Base Currency',
    targetCurrency: 'Target Currency',
    rate: 'Rate',
  },
  feeCalculation: {
    distributorId: 'Distributor',
    periodRange: 'Period',
  },
  calculatedFees: {
    id: 'ID',
    isin: 'Product ISIN',
    productName: 'Product Description',
    actualPayment: 'Actual Payment',
    status: 'Status',
    period: 'Period',
    currency: 'Currency',
    linkedId: 'Linked ID',
    calculatedAmount: 'Calculated Amount',
    revisedAmount: 'Revised Payment',
    originalAmount: 'Original Payment',
    dbTraderNo: 'DB Trader No.',
    summitCounterpartyCode: 'Summit Counterparty Code',
    bookingReference: 'Booking Reference',
    bookedPaymentDate: 'Booked Payment Date',
    comment: 'Comment',
    balance: 'Effective Balance',
    calculationDate: 'Legal Date',
    price: 'Effective Price',
    rateForThePeriod: 'Effective Rate',
    periodStartDate: 'Period Start Date',
    periodEndDate: 'Period End Date',
    adjusted: 'Adjusted',
    estimated: 'Estimated',
    // ReminderDate -- TODO: Confirm with Supriya
    type: 'Type', // Supplement Agreement Fee Type
    category: 'Category', // Supplement Agreement Fee Category
    createdBy: 'Created By',
    createdAt: 'Created At',
    approvedBy: 'Approved By',
    approvedAt: 'Approved At',
    dataVersion: 'Version',
    statementDate: 'Statement Date',
    desiredPaymentDate: 'Desired Payment Date',
    trader: 'Trader',
    tradeSupport: 'Trade Support',
    lastUpdatedAt: 'Last Updated At',
    lastUpdatedBy: 'Last Updated By',
    calculatedAmountInReportingCurrency: 'Actual Amount in Euro',
    revisedAmountInReportingCurrency: 'Revised Amount in Euro',
    originalAmountInReportingCurrency: 'Original Amount in Euro',
    salesTeam: 'Sales Team', // TODO: Confirm with Supriya
    // Sales Team -- TODO: Confirm with Supriya
    balanceDate: 'Balance Date',
    priceDate: 'Price Date',
    // Multiple Balances price -- TODO: Confirm with Supriya
    exchangeRate: 'Exchange Rate',
    exchangeRateDate: 'Exchange Rate Date'
  },
  tradeDeliveryReference: {
    id: 'ID',
    backofficeSystem: 'Backoffice System',
    deliveryReference: 'Reference',
    notional: 'Notional',
    settlementMethod: 'Settlement Method',
    createdBy: 'Entered User',
    createdDate: 'Entered Date',
  }
} as const satisfies FeesLabels;
